/* eslint-disable no-underscore-dangle */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { parseInt } from 'lodash';
import { msalInstance } from 'Utils/auth/authProvider';
const nonstrictWindow = window;
const getEnv = (name) => process.env[name] || nonstrictWindow[name];
const defaultRetriesCount = 3;
const defaultRetryDelayBase = 1000;
const defaultRetryDelayExp = 2;
const defaultTimeout = 200 * 1000;
const HTTP_UNAUTHORIZED = 401;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const RETRY_STATUSES = [HTTP_INTERNAL_SERVER_ERROR];
export const getBaseURLApp = () => getEnv('BASE_URL_APP') || 'https://localhost';
export const getBaseURL = () => getEnv('BASE_URL') || 'https://localhost';
export const getBaseURLMidTier = () => getEnv('BASE_URL_MID_TIER') || getBaseURL();
export const getBaseURLTS = () => getEnv('BASE_URL_TS');
export const getBaseURLController = () => getEnv('BASE_URL_CONTROLLER') || getBaseURL();
export const getBaseURLIOC = () => getEnv('BASE_URL_IOC');
export const getAlmondBasename = () => getEnv('ALMOND_BASENAME') || 'almond';
export const getBaseURLActivityAPI = () => getEnv('BASE_URL_ACTIVITY_API');
export const getBaseURLAnnotationAPI = () => getEnv('BASE_URL_ANNOTATION_API');
export const getEnvironmentName = () => getEnv('ENV_NAME') || 'Localhost';
export const numberFromConfig = (input, defaultValue) => {
    const value = parseInt(input);
    return Number.isNaN(value) ? defaultValue : value;
};
export const getRetriesCount = () => numberFromConfig(getEnv('HTTP_RETRIES_COUNT'), defaultRetriesCount);
export const getRetryDelayBase = () => numberFromConfig(getEnv('HTTP_RETRY_DELAY_BASE'), defaultRetryDelayBase);
export const getRetryDelayExp = () => numberFromConfig(getEnv('HTTP_RETRY_DELAY_EXP'), defaultRetryDelayExp);
export const isHttpErrorCode = (err, code) => {
    if (err.isAxiosError) {
        const axiosError = err;
        return axiosError.response?.status === code;
    }
    return false;
};
export const paramsSerializer = (params) => Object.entries(params)
    .filter(([, value]) => value)
    .map(([key, value]) => {
    if (Array.isArray(value)) {
        return value.map((v) => `${key}=${encodeURIComponent(v)}`).join('&');
    }
    return `${key}=${encodeURIComponent(value)}`;
})
    .join('&');
export const createExponentialDelayer = (base, exp) => (retryNumber = 0) => {
    const delay = base * exp ** retryNumber;
    const randomSum = delay * 0.2 * Math.random(); // 0-20% of the delay
    return delay + randomSum;
};
// Streaming
export const parseStreamResponseIntoHash = (response) => {
    const { data } = response;
    let items;
    if (typeof data === 'string') {
        items = data.split('\n').reduce((acc, line) => {
            try {
                return { ...acc, ...JSON.parse(line).items };
            }
            catch {
                return acc;
            }
        }, {});
    }
    else if (typeof data === 'object') {
        items = data.items;
    }
    return items;
};
export const parseStreamResponseIntoList = (response) => {
    const { data } = response;
    let items;
    if (typeof data === 'string') {
        items = data.split('\n').reduce((acc, line) => {
            try {
                return [...acc, JSON.parse(line)];
            }
            catch {
                return acc;
            }
        }, []);
    }
    else if (typeof data === 'object') {
        items = Array.isArray(data) ? data : [data];
    }
    return items;
};
export const buildRetrieveError = (error) => {
    if (error?.isAxiosError) {
        return {
            status: error?.response?.status ?? 0,
            message: error?.response?.data?.message ?? 'Unhandled error'
        };
    }
    return {
        status: 0,
        message: error?.toString() ?? 'Unhandled error'
    };
};
export class AuthTokenHandler {
    scopes;
    tokenKey;
    constructor(scopes, tokenKey) {
        this.scopes = scopes;
        this.tokenKey = tokenKey;
    }
    // Make a call to get auth token or return the same promise as an in-progress call
    async retrieveToken() {
        const account = msalInstance.getActiveAccount();
        if (!account) {
            throw Error('No active account! Verify a user has been signed in and setActiveAccount has been called.');
        }
        const silentRequestParams = {
            scopes: this.scopes,
            account
        };
        try {
            const response = await msalInstance.acquireTokenSilent(silentRequestParams);
            return response.accessToken;
        }
        catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                await msalInstance.acquireTokenRedirect(silentRequestParams);
            }
        }
    }
    async refreshToken() {
        const token = await this.retrieveToken();
        localStorage.setItem(this.tokenKey, token);
        return token;
    }
    async getToken() {
        const localToken = localStorage.getItem(this.tokenKey);
        if (!localToken) {
            return this.refreshToken();
        }
        return localToken;
    }
}
export const createAxios = (scopes, baseURL, tokenKey) => {
    const instance = axios.create({
        baseURL,
        paramsSerializer,
        timeout: defaultTimeout
    });
    const authTokenHandler = new AuthTokenHandler(scopes, tokenKey);
    // Request interceptor for API calls
    instance.interceptors.request.use(async (config) => {
        const token = await authTokenHandler.getToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
    }, (error) => Promise.reject(error));
    // Response interceptor for API calls
    instance.interceptors.response.use((response) => response, async (error) => {
        const config = error.config;
        if (!config)
            return Promise.reject(new Error('Some error'));
        if ((!error.response || error.response.status === HTTP_UNAUTHORIZED) && !config._retry) {
            return authTokenHandler.refreshToken().then((token) => {
                config._retry = true;
                config.headers.Authorization = `Bearer ${token}`;
                return axios(config);
            });
        }
        return Promise.reject(error);
    });
    const retries = getRetriesCount();
    if (retries) {
        axiosRetry(instance, {
            retries,
            // Only retry when there's no response status code (request cancelled by the client)
            // Or the error is 500 (backend often responds with 500's randomly)
            retryCondition: (error) => {
                const responseStatus = error?.response?.status;
                return !responseStatus || RETRY_STATUSES.includes(responseStatus);
            },
            // Otherwise axios timeout will be re-used as a single timeline for all requests
            shouldResetTimeout: true,
            retryDelay: createExponentialDelayer(getRetryDelayBase(), getRetryDelayExp())
        });
    }
    return instance;
};
